import React, { useState } from "react";
import FormDatePicker from "../../components/common/form-date-picker";
import useHolidays from "../../hooks/use-holidays";

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDateTH = (str) => {
  if (!str) return null;
  const [d, m, y] = String(str).split("/").map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

const isWeekend = (dateStr) => {
  const d = parseDateTH(dateStr);
  if (!d || isNaN(d.getTime())) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
};

const formatDateTH = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const addDays = (dateStr, n) => {
  const d = parseDateTH(dateStr);
  if (!d) return null;
  d.setDate(d.getDate() + n);
  return d;
};

// ── หาวันทำงานถัดไป: เลื่อนไปทีละวัน ข้ามเสาร์-อาทิตย์-วันหยุด จนกว่าจะเจอวันทำงานจริง ──
const getNextWorkingDate = (dateStr, isDateAvailable) => {
  const d = parseDateTH(dateStr);
  if (!d) return null;
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6 || !isDateAvailable(d));
  return d;
};

// ── หาวันทำงานก่อนหน้า: เลื่อนถอยหลังทีละวัน ข้ามเสาร์-อาทิตย์-วันหยุด จนกว่าจะเจอวันทำงานจริง ──
const getPrevWorkingDate = (dateStr, isDateAvailable) => {
  const d = parseDateTH(dateStr);
  if (!d) return null;
  do {
    d.setDate(d.getDate() - 1);
  } while (d.getDay() === 0 || d.getDay() === 6 || !isDateAvailable(d));
  return d;
};

// ── Component ─────────────────────────────────────────────────────────────────
const HolidayConflictEdit = ({ conflictDates = [], onBack, onCancel, onSave }) => {
  const { isDateAvailable } = useHolidays();

  const isWorkingDay = (date) => {
  const day = new Date(date).getDay();
  if (day === 0 || day === 6) return false;
  return isDateAvailable(date);
};

  const [resolvedMap, setResolvedMap] = useState(() =>
    Object.fromEntries(conflictDates.map((d) => [d, { choice: "", customDate: null }]))
  );

  const updateResolved = (original, patch) =>
    setResolvedMap((prev) => ({
      ...prev,
      [original]: { ...prev[original], ...patch },
    }));

  const getResolvedDate = (original, state) => {
    if (state.choice === "next")   return getNextWorkingDate(original, isDateAvailable);
    if (state.choice === "prev")   return getPrevWorkingDate(original, isDateAvailable);
    if (state.choice === "custom") return state.customDate ? new Date(state.customDate) : null;
    return null;
  };

  const handleSave = () => {
    const result = Object.fromEntries(
      conflictDates.map((d) => {
        const resolved = getResolvedDate(d, resolvedMap[d]);
        return [d, resolved ? formatDateTH(resolved) : null];
      })
    );
    onSave?.(result);
  };

  const allValid = conflictDates.every((d) => {
    const state = resolvedMap[d];
    if (!state?.choice) return false;
    if (state.choice === "custom") return !!state.customDate;
    return true;
  });

  return (
    <div className="page-wrapper">
      <div className="rs-top-bar">
        <div>
          <div className="rs-page-title">แก้ไขวันที่เกิดคำขอ</div>
          <div className="page-subtitle">เปลี่ยนแปลงวันที่เพื่อสร้างคำขอใหม่</div>
        </div>
        <button type="button" className="rp-cancel-link" onClick={onCancel}>
          <i className="mdi mdi-rotate-left" /> Cancel recurring setup
        </button>
      </div>

      <div className="hce-list">
        {conflictDates.map((original) => {
          const state    = resolvedMap[original];
          const nextDate = formatDateTH(getNextWorkingDate(original, isDateAvailable));
          const prevDate = formatDateTH(getPrevWorkingDate(original, isDateAvailable));

          return (
            <div key={original} className="hce-card">
              <div className="hce-col">
                <div className="hce-col-title">จากวันที่</div>
                <div className="hce-date-display">
                  <i className="mdi mdi-calendar hce-date-icon" />
                  {original}
                </div>
              </div>

              <div className="hce-col hce-col--right">
                <div className="hce-col-title">เป็นวันที่</div>
                <div className="hce-suggest-label">วันที่แนะนำ</div>

                <div className="hce-radio-group">
                  {[
                    { value: "next",   label: `เลื่อนไปวันถัดไป → ${nextDate}` },
                    { value: "prev",   label: `เลื่อนไปวันก่อนหน้า → ${prevDate}` },
                    { value: "custom", label: "เลือกวันอื่น" },
                  ].map((opt) => (
                    <label key={opt.value} className="rs-radio-label">
                      <input
                        type="radio"
                        name={`choice-${original}`}
                        checked={state.choice === opt.value}
                        onChange={() => updateResolved(original, { choice: opt.value })}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>

                {!state.choice && (
                  <div className="hce-invalid-feedback" style={{ color: "#ef4444", fontSize: 13, marginTop: 4 }}>
                    กรุณาเลือกวันที่ใหม่สำหรับวันที่ชนวันหยุดนี้
                  </div>
                )}

                {state.choice === "custom" && (
                  <div className="hce-datepicker-wrap">
                    <div className="hce-suggest-label">เลื่อนไปวันที่</div>
                    <FormDatePicker
                      value={state.customDate}
                      onChange={(date) => updateResolved(original, { customDate: date })}
                      isInvalid={!state.customDate}
                      filterDate={isWorkingDay}
                      disablePast
                    />
                    {!state.customDate && (
                      <div className="hce-invalid-feedback" style={{ color: "#ef4444", fontSize: 13, marginTop: 4 }}>
                        กรุณาเลือกวันที่
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="form-footer">
        <div>
          {!allValid && (
            <div style={{ color: "#ef4444", fontSize: 13 }}>
              <i className="mdi mdi-alert-circle-outline" /> กรุณาเลือกวันที่ใหม่ให้ครบทุกรายการก่อนบันทึก
            </div>
          )}
        </div>
        <div className="footer-actions">
          <button type="button" className="btn btn-secondary btn-back" onClick={onBack}>
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!allValid}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default HolidayConflictEdit;